import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';
import { Check, Star, X } from 'lucide-react';

interface PricingCardProps {
  title: string;
  price: string;
  specs: string[];
  buyLink?: string;
  isPopular?: boolean;
  isYearly?: boolean;
  variant?: 'standard' | 'primary';
}

export function PricingCard({
  title,
  price,
  specs,
  buyLink,
  isPopular = false,
  isYearly = false,
  variant = 'standard'
}: PricingCardProps) {
  const { t } = useTranslation();

  if (!Array.isArray(specs)) {
    return null;
  }

  return (
    <div
      className={`relative bg-white rounded-lg shadow-md border ${
        isPopular ? 'border-blue-500 shadow-lg' : 'border-gray-200'
      }`}
    >
      {isPopular && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <div className="bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-medium flex items-center gap-1">
            <Star className="w-4 h-4" />
            <span>Popular</span>
          </div>
        </div>
      )}

      <div className="p-6">
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <div className="mb-4">
          <span className="text-2xl font-bold">{price}</span>
          <span className="text-gray-600 ml-1">
            {isYearly ? t('common.perYear') : t('common.perMonth')}
          </span>
        </div>

        {/* elenco specifiche */}
        <ul className="space-y-3 mb-6">
          {specs.map((spec, index) => {
            const normalized = spec.trim().toLowerCase();

            // NEGATIVO SOLO SE C'È CHIARAMENTE "non incluso / not included"
            const isNotIncluded =
              normalized.includes('non incluso') ||
              normalized.includes('non inclusi') ||
              normalized.includes('non inclusa') ||
              normalized.includes('non incluse') ||
              normalized.includes('not included');

            return (
              <li key={index} className="flex items-start gap-2">
                {isNotIncluded ? (
                  <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                ) : (
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                )}
                <span
                  className={`text-sm ${
                    isNotIncluded ? 'text-gray-500' : 'text-gray-600'
                  }`}
                >
                  {spec}
                </span>
              </li>
            );
          })}
        </ul>

        {buyLink ? (
          <a
            href={buyLink}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full"
          >
            <Button
              variant={variant === 'primary' ? 'primary' : 'secondary'}
              className="w-full"
            >
              {t('common.buyNow')}
            </Button>
          </a>
        ) : (
          <Button
            variant={variant === 'primary' ? 'primary' : 'secondary'}
            className="w-full"
          >
            {t('common.buyNow')}
          </Button>
        )}
      </div>
    </div>
  );
}
